import size from "lodash-es/size";
import { v4 as uuid } from "uuid";
import type { Constraint } from "../components/RulesUI/Constraints/constraints.types";
import type { Process } from "@certa/studio-core/src/types/process.types";
import type { RuleMap } from "@certa/studio-core/src/types/rules.types";
import { CompareType } from "../components/RulesUI/Constraints/constraints.types";
import { ruleActionSerialize } from "./ruleActions";

/**
 * Converts the UI data structure into the system specific schema.
 * @param rules
 */
export const serializeSync = <T = Constraint>({
  rulesMap,
  conditionLess = false,
  conditionOptional = false,
  metaJSON
}: {
  rulesMap: RuleMap;
  conditionLess: boolean;
  conditionOptional: boolean;
  metaJSON: Process;
}): Record<string, T> | undefined => {
  const ruleValues = Object.values(rulesMap);
  if (!ruleValues.length) return undefined;

  if (conditionLess) {
    // All rules will not have any conditions, which means that we simply take all the rules,
    // extract their Actions and prepare the final JSON from it

    const constraints = ruleValues.reduce(
      (accumulator, currentRule, currentRuleIndex) => {
        const currentRuleActions = ruleActionSerialize(
          currentRule.actions,
          // @ts-expect-error TS5 upgrade
          currentRule.ruleType
        );
        return {
          ...accumulator,
          [currentRuleIndex]: currentRuleActions
        };
      },
      {}
    );

    return constraints;
  }
  let finalJSON: Record<string, T> | Record<string, any> = {};
  const uniqueGroups = new Set<number>([]);

  let totalConstraints = 0;

  // For every rule that we have we simply their conditions recursively
  for (const ruleIndex in ruleValues) {
    const rule = ruleValues[ruleIndex];
    // @ts-expect-error TS5 upgrade
    const ruleActions = ruleActionSerialize(rule.actions, rule.ruleType);
    const { topLevelGroupId } = rule;

    // Simplify constraints into system specific JSON schema
    const constraints =
      simplifyGroup({
        groupId: topLevelGroupId,
        levelPrefix: `${ruleIndex + 1}`,
        metaJSON
      }) || [];

    // this makes eslint happy - https://eslint.org/docs/rules/no-loop-func
    const _totalConstraints = totalConstraints;

    const constraintsFilteredObject = constraints.reduce(
      (accumulator, current, index) => {
        // Ignore any rules where first compare field is not defined
        if (!current.first_compare_field) return accumulator;

        // Put all the unique IDs (string) assigned by front-end into a Set, so that
        // we can later assign them numerical values in the next pass, which is
        // also required by the system.
        uniqueGroups.add(current.compare_group_uid);

        // We're collating all those into accumulator
        return {
          ...accumulator,
          [`${_totalConstraints + index + 1}`]: {
            ...current,
            ...(conditionOptional ? {} : ruleActions)
          }
        };
      },

      // Initial value of accumulator JSON
      {}
    );

    if (conditionOptional) {
      // The conditional optional field will ignore the condition and just add the
      // action with index if there are no constraints available for the existing rule
      finalJSON = {
        ...finalJSON,
        [ruleIndex]: {
          ...ruleActions,
          ...(size(constraintsFilteredObject)
            ? { constraints: constraintsFilteredObject }
            : {})
        }
      };
    } else {
      // Here we finally collate everything into a single JSON while generating
      // unique keys for each and every constraint
      finalJSON = {
        ...finalJSON,
        ...constraintsFilteredObject
      };
    }

    totalConstraints += constraints.length;
  }

  const uniqueGroupsArr = Array.from(uniqueGroups); // convert the Set to Array

  // And now for every key in the collated JSON we replace the string IDs to index based
  // IDs, which is acceptable format by the system (Backend)
  if (conditionOptional) {
    Object.keys(finalJSON).forEach(key => {
      if (finalJSON[key].constraints && size(finalJSON[key].constraints)) {
        Object.keys(finalJSON[key].constraints).forEach(key2 => {
          finalJSON[key].constraints[key2] = {
            ...finalJSON[key].constraints[key2],
            compare_group:
              uniqueGroupsArr.indexOf(
                finalJSON[key].constraints[key2].compare_group_uid
              ) + 1
          };

          // deleting the key, since that's not part of system schema for constraints
          delete finalJSON[key].constraints[key2].compare_group_uid;
        });
      }
    });
  } else {
    Object.keys(finalJSON).forEach(key => {
      finalJSON[key] = {
        ...finalJSON[key],
        compare_group:
          uniqueGroupsArr.indexOf(finalJSON[key].compare_group_uid) + 1
      };

      // deleting the key, since that's not part of system schema for constraints
      delete finalJSON[key].compare_group_uid;
    });
  }

  return finalJSON;
};

/**
 * Returns a single array of all the constraints within a group. Can be called
 * recursively to keep simplifying each groups and it's sub-groups to form a
 * linear data structure, from a nested one.
 * @param groupId
 * @param levelPrefix
 * @param groups hashmap that contains all the groups by their groupId (uuid)
 * @param conditions hashmap that contains all the conditions by their conditionId (uuid)
 */
const simplifyGroup = ({
  groupId,
  levelPrefix,
  metaJSON
}: {
  groupId: string;
  levelPrefix: string;
  metaJSON: Process;
}) => {
  let constraints: Constraint[] = [];

  const currentGroup = metaJSON.conditionGroups[groupId];

  if (!currentGroup) return [];

  const nestedConstraints: Record<string, Constraint[]> = {};
  if (currentGroup.groups.length) {
    for (const groupIndex in currentGroup.groups) {
      const constraints = simplifyGroup({
        groupId: currentGroup.groups[groupIndex],
        levelPrefix: `${levelPrefix}${groupIndex}`,
        metaJSON
      });

      nestedConstraints[`${levelPrefix}${groupIndex}`] = constraints!;
    }
  }

  const isAll = currentGroup.compareType === CompareType.ALL;
  const commonUID = uuid();
  for (const conditionId of currentGroup.conditions) {
    const condition = metaJSON.conditions[conditionId];
    if (!condition) return;
    const constraint: Constraint = {
      first_compare_field: condition.firstCompareField,
      operator: condition.operator,
      second_compare_field: condition.secondCompareField,
      compare_value: condition.compareValue,
      compare_group: 0, // temporarily as the real one will be assigned at the final point
      compare_group_uid: isAll ? commonUID : uuid() // assign unique uuid if the compare type is ANY
      // compare_group: Number(isAll ? levelPrefix : `${levelPrefix}00${index}`) // maybe use uuid in place of this
    };
    if (!constraint.second_compare_field && !constraint.compare_value)
      constraint.compare_value = "";
    constraints.push(constraint);
  }

  let updatedConstraints: Constraint[] = []; // to be only filled for "ALL" case that had nested groups of "ANY"
  Object.keys(nestedConstraints).forEach(levelKey => {
    const _constraints = nestedConstraints[levelKey];
    if (isAll) {
      // ALL
      const uniqueGroups: Record<string, Constraint[]> = {};
      _constraints.forEach(conditionFromGroup => {
        uniqueGroups[conditionFromGroup.compare_group_uid] =
          uniqueGroups[conditionFromGroup.compare_group_uid] || [];
        uniqueGroups[conditionFromGroup.compare_group_uid].push(
          conditionFromGroup
        );
      });

      Object.keys(uniqueGroups).forEach(groupKey => {
        updatedConstraints = [
          ...updatedConstraints,
          ...uniqueGroups[groupKey],
          ...constraints.map(constraint => ({
            ...constraint,
            compare_group_uid: groupKey
          }))
        ];
      });
    } else {
      // ANY
      if (!_constraints || !Array.isArray(_constraints)) {
        // eslint-disable-next-line no-console
        console.log(
          `Cannot find constraints for groupId: ${groupId} | levelPrefix: ${levelPrefix}`
        );
      }
      constraints = [...constraints, ..._constraints];
    }
  });

  if (updatedConstraints.length) {
    // Only if we have nested groups of ANY in an ALL group, we will return updated constraints
    return updatedConstraints;
  } else {
    // Otherwise the regular set of constraints that we get are all we need
    return constraints;
  }
};
